import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { trackAddToCart, trackRemoveFromCart } from '@/utils/analytics';

const CartContext = createContext(null);
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function getSessionId() {
  let sid = localStorage.getItem('outlet_session_id');
  if (!sid) {
    sid = `sess_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
    localStorage.setItem('outlet_session_id', sid);
  }
  return sid;
}

function trackEvent(eventType, data = {}) {
  const sessionId = getSessionId();
  axios.post(`${API}/track/event`, {
    session_id: sessionId,
    event_type: eventType,
    user_email: localStorage.getItem('user_email') || null,
    user_name: localStorage.getItem('user_name') || null,
    data
  }).catch(() => {});
}

export { trackEvent, getSessionId };

export function CartProvider({ children }) {
  const [cart, setCart] = useState(() => {
    const saved = localStorage.getItem('cart');
    return saved ? JSON.parse(saved) : [];
  });

  useEffect(() => {
    localStorage.setItem('cart', JSON.stringify(cart));
  }, [cart]);

  const sendCartUpdate = useCallback((newCart) => {
    const total = newCart.reduce((s, i) => s + i.price * i.quantity, 0);
    trackEvent('cart_update', {
      items: newCart.map(i => ({ product_id: i.product_id, name: i.name, price: i.price, quantity: i.quantity, size: i.size, color: i.color })),
      total,
      item_count: newCart.reduce((s, i) => s + i.quantity, 0)
    });
  }, []);

  const addToCart = (product, size, color, quantity = 1) => {
    setCart(prev => {
      const existingIndex = prev.findIndex(
        item => item.product_id === product.product_id && item.size === size && item.color === color
      );

      let newCart;
      if (existingIndex >= 0) {
        newCart = [...prev];
        newCart[existingIndex].quantity += quantity;
      } else {
        newCart = [...prev, {
          product_id: product.product_id,
          name: product.name,
          price: product.price,
          original_price: product.original_price,
          image: product.images[0],
          size,
          color,
          quantity
        }];
      }

      trackEvent('add_to_cart', {
        product_id: product.product_id,
        name: product.name,
        price: product.price,
        size, color, quantity,
        items: newCart.map(i => ({ product_id: i.product_id, name: i.name, price: i.price, quantity: i.quantity, size: i.size, color: i.color })),
        total: newCart.reduce((s, i) => s + i.price * i.quantity, 0)
      });
      trackAddToCart(product, size, color, quantity);

      return newCart;
    });
  };

  const removeFromCart = (product_id, size, color) => {
    setCart(prev => prev.filter(
      item => !(item.product_id === product_id && item.size === size && item.color === color)
    ));
  };

  const updateQuantity = (product_id, size, color, quantity) => {
    if (quantity <= 0) {
      removeFromCart(product_id, size, color);
      return;
    }

    setCart(prev => prev.map(item => {
      if (item.product_id === product_id && item.size === size && item.color === color) {
        return { ...item, quantity };
      }
      return item;
    }));
  };

  const clearCart = () => setCart([]);

  const cartTotal = cart.reduce((sum, item) => sum + (item.price * item.quantity), 0);
  const cartCount = cart.reduce((sum, item) => sum + item.quantity, 0);

  return (
    <CartContext.Provider value={{
      cart,
      addToCart,
      removeFromCart,
      updateQuantity,
      clearCart,
      cartTotal,
      cartCount
    }}>
      {children}
    </CartContext.Provider>
  );
}

export function useCart() {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
}
